import { MouseEvent, useMemo } from 'react'
import type { RenderableFilterOption } from 'shared/types/util'
import { useSelector, useDispatch } from 'react-redux'
import { Typography, List, ListItem } from '@vp/swan'

import { FilterCheckboxList } from '~/app/features/Gallery/components/Filters/Shared/FilterCheckboxList'
import { ShowAllCategoriesButton } from '~/app/features/Gallery/components/Filters/FacetedTaxonomy/ShowAllCategoriesButton'
import { buildRefinement, refinementRemove } from '~/app/store/refinement'
import { REFINEMENT_DIMENSION } from 'shared/constants'
import { scrollUpToElement } from '~/app/utils/scrollToElement'
import { galleryHeaderId } from '~/app/features/Gallery/components/Header/ActionHeader/constants'
import { isFilterModalHidden } from '~/app/features/Gallery/components/Header/ActionHeader/components/FilterMenu/utils'
import { selectedChildrenSelectorFactory } from '~/app/features/Gallery/components/Filters/util'
import { useRedirectToL0 } from '~/app/features/Gallery/hooks/RedirectToL0/useRedirectToL0'
import { ThunkDispatch } from '~/app/store/interface'
import type { State } from 'shared/types/gallery/state'

export interface L2FilterProps {
  id: string
  options: RenderableFilterOption[]
  refinementDimension: REFINEMENT_DIMENSION
  title: string
}

export const L2Filter = (props: L2FilterProps) => {
  const { id, options, refinementDimension, title } = props
  const dispatch = useDispatch<ThunkDispatch>()
  const redirectToL0 = useRedirectToL0()

  const selectedChildrenSelector = useMemo(() => selectedChildrenSelectorFactory(), [])
  const selectedValues = useSelector((state: State.GlobalState) => selectedChildrenSelector(state, id))

  const handleShowAllClick = (event: MouseEvent): void => {
    event.preventDefault()

    const refinements = Object.keys(selectedValues).map((c) => buildRefinement(c, refinementDimension))

    refinements.push(buildRefinement(id, refinementDimension))

    if (redirectToL0([], refinements)) {
      return
    }

    dispatch(refinementRemove(refinements))
    if (isFilterModalHidden()) {
      scrollUpToElement(galleryHeaderId)
    }
  }

  return (
    <List skin="minimal">
      <ListItem>
        <ShowAllCategoriesButton onClick={handleShowAllClick} />
      </ListItem>
      <ListItem>
        <Typography fontSize="small" fontWeight="bold">
          {title}
        </Typography>
      </ListItem>
      <ListItem>
        <FilterCheckboxList options={options} refinementDimension={refinementDimension} selectedValues={selectedValues} title={title} />
      </ListItem>
    </List>
  )
}

L2Filter.displayName = 'L2Filter'
